import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { Check, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../../../../ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../../../../ui/command";
import Button from "../../../../Button";
import Spinner from "../../../../Spinner";
import useApi from "../../../../../../utils/api";
import { cn } from "../../../../../../lib/utils";

function TemplateSelect({
    user,
    selectedTemplate,
    setSelectedTemplate,
    setExpandedDays,
    setExpandedEvents,
}: any) {
    const { t } = useTranslation();
    const [search, setSearch] = useState("");
    const { authGet } = useApi();

    // Templates list with infinite scroll + search
    const {
        data: templatesPages,
        isLoading: loadingTemplates,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
    } = useInfiniteQuery({
        queryKey: ["/schedule-templates", { search, /**userId: user?.id */ }],
        queryFn: async ({ queryKey, pageParam }: any) => {
            const [endpoint, params] = queryKey;
            return authGet(endpoint, {
                params: {
                    ...(params.search ? { search: params.search } : {}),
                    userId: params.userId,
                    page: pageParam,
                    perPage: 20,
                },
            });
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage: any, allPages: any[]) => {
            const loaded = allPages.reduce((acc, p) => acc + (p?.result?.length ?? 0), 0);
            return loaded < (lastPage?.totalItems ?? 0) ? allPages.length + 1 : undefined;
        },
        enabled: !!user?.id,
    });

    const { ref: sentinelRef, inView } = useInView({ threshold: 0 });

    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) fetchNextPage();
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    const templates: any[] = useMemo(() => {
        return templatesPages?.pages?.flatMap((page: any) => page?.result ?? []) ?? []
    }, [templatesPages]);

    // Auto-select first template once loaded
    useEffect(() => {
        if (!selectedTemplate && templates.length > 0) {
            setSelectedTemplate({ id: templates[0].id, name: templates[0].name });
        }
    }, [templates, selectedTemplate]);
    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button variant="outline" className="tw-w-fit tw-justify-between tw-font-normal tw-h-9">
                    <span className={cn("tw-truncate tw-text-sm", !selectedTemplate && "tw-text-muted-foreground")}>
                        {selectedTemplate?.name ?? t("schedule-templates")}
                    </span>
                    <ChevronDown size={14} className="tw-shrink-0 tw-text-muted-foreground tw-ml-2" />
                </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="tw-p-1 tw-w-72">
                <Command shouldFilter={false}>
                    <CommandInput
                        value={search}
                        placeholder={`${t("search")}...`}
                        onValueChange={setSearch}
                    />
                    <CommandList>
                        <CommandEmpty>
                            {loadingTemplates ? <Spinner /> : t("no-result")}
                        </CommandEmpty>
                        <CommandGroup>
                            {templates.map((item: any) => {
                                const checked = selectedTemplate?.id === item.id;
                                return (
                                    <CommandItem
                                        key={item.id}
                                        value={item.id}
                                        className={cn("tw-cursor-pointer tw-flex tw-items-center tw-gap-2", checked && "tw-bg-primary/10")}
                                        onSelect={() => {
                                            setSelectedTemplate({ id: item.id, name: item.name });
                                            setExpandedDays({});
                                            setExpandedEvents({});
                                        }}
                                    >
                                        {checked
                                            ? <Check size={14} className="tw-shrink-0 tw-text-primary" />
                                            : <div className="tw-w-3.5 tw-shrink-0" />
                                        }
                                        <span className="tw-truncate tw-text-sm">{item.name}</span>
                                    </CommandItem>
                                );
                            })}
                            {/* Infinite scroll sentinel */}
                            <div ref={sentinelRef} className="tw-py-1 tw-flex tw-justify-center">
                                {isFetchingNextPage && <Spinner />}
                            </div>
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

export default TemplateSelect;
